import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  BadgePercent,
  Check,
  Gift,
  Plus,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/button';
import { Combobox } from '@/shared/components/ui/combobox';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/shared/components/ui/tooltip';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { ErrorDialog } from '@/shared/components/ui/status-dialog';
import { formatIvaRate } from '@/shared/countries';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import { Input } from '@/shared/components/ui/input';
import { cn } from '@/shared/lib/cn';
import { formatMoney, parseMoney } from '@/shared/lib/money';
import { round2 } from './lib/pagosCalc';
import { cajaBase } from '@/shared/lib/cajasEfectivo';
import {
  listActiveCotizableArticulos,
  articuloLlevaNroSerie,
  type ArticuloItem,
} from '@/shared/api/controllers/articulos';
import {
  calcStockCotizable,
  calcStockDisponible,
  listActiveInventario,
  type InventarioItem,
} from '@/shared/api/controllers/inventario';
import { listDescuentosByVendedorMes } from '@/shared/api/controllers/descuentos';
import { listPendingByOperacion } from '@/shared/api/controllers/autorizacionDescuentos';
import { getRegalosMes, getUsadoContext } from '@/shared/api/controllers/ventas';
import { listAllParametrosComDesc } from '@/shared/api/controllers/parametrosComDesc';
import { editarVenta, type EditVentaLinePayload } from '@/shared/api/controllers/ventas';
import {
  listActiveComprobantesByOperacion,
  type ComprobantePagoItem,
} from '@/shared/api/controllers/comprobantePagos';
import { ApiError } from '@/shared/api/client';
import { staleConfigError, staleConfigMessage } from './lib/submitErrors';
import { FechaIngresoPicker, PorIngresarBadge, useFechasPorIngresar } from './components/FechaIngreso';
import {
  ANCHO_LISTA_ARTICULOS,
  CLASE_ITEM_POR_INGRESAR,
  CLASE_ITEM_SIN_STOCK,
  estadoSiguienteUnidad,
  estadosPorLinea,
} from './lib/porIngresar';
import { useRealtimeInvalidate } from '@/shared/realtime/useRealtimeInvalidate';
import { calcInvoiceTotals, calcLineTotals, type IvaRate } from './lib/calc';
import { conIvaSegunCliente, ivaAEnviar, NOTA_EXPORTACION_SIN_IVA } from './lib/ivaExterior';
import {
  countMonthlyDescuentos,
  evaluateLineDiscount,
  readAllowanceLimits,
  readBracketPercents,
  readRegaloMensualLimit,
} from './lib/discounts';
import { isArticuloRegalable, useRegaloMinimoThreshold } from './lib/useRegaloMinimo';
import { MoneyCell, type Moneda } from './components/MoneyCell';
import { ProcesandoOverlay } from './components/ProcesandoOverlay';
import { DescuentoCell } from './components/DescuentoCell';
import {
  GarantiaBadge,
  GarantiaFechaDialog,
  GarantiaToggle,
  mesesGarantiaDe,
  SIN_GARANTIA,
  type GarantiaLinea,
} from './components/Garantia';
import {
  DescuentoAprobacionDialog,
  type AuthDialogState,
} from './components/DescuentoAprobacionDialog';

/**
 * Dialog that lets the user edit an existing cotización's line items
 * before it's been accepted. Mirrors the line-editing UX of
 * `NuevaCotizacionPage` (add/remove articles, inline qty + descuento,
 * approval-motivo modal) but scopes the state to a single venta and
 * persists via `POST /api/ventas/:id/editar` so stale descuento and
 * autorización rows get soft-cancelled server-side.
 */
export type EditorLineSeed = GarantiaLinea & {
  /** SharePoint DetalleOperaciones id — lets the backend PATCH in place. */
  detalleId: string;
  articuloId: string;
  articulo: string;
  monto: number;
  cantidad: number;
  iva: IvaRate;
  descuento: number;
  esRegalo: boolean;
  campaignPct: number | null;
  /** yyyy-mm-dd. Ingreso estimado ya elegido para stock por ingresar (informativo). */
  fechaEstimadaIngreso?: string | null;
  /** Existing motivo text (if the line was already flagged pending approval). */
  motivo?: string;
  /**
   * Descuento value that's already been approved on this line (the
   * `Descuento_RO` of the live `Status_DO=Activo` row). When the editor
   * shows the line at this exact value, no re-authorization is required —
   * either it never needed auth, or it was already approved as-is or via
   * a `Descuento Reemplazo` from the Aprobaciones screen. As soon as the
   * user changes the value, the standard allowance check kicks back in.
   */
  approvedDescuento?: number;
};

export function EditarCotizacionDialog({
  open,
  onClose,
  ventaId,
  moneda,
  vendedorApp,
  initialLines,
  campaignByArticulo,
  porcentajeFactura = 100,
  recargoMpPct = 0,
  clienteExterior = false,
}: {
  open: boolean;
  onClose: () => void;
  ventaId: string;
  moneda: Moneda;
  vendedorApp: string;
  initialLines: EditorLineSeed[];
  /** Active campaign pct keyed by articuloId — passed through from the
   *  detail page so brand-new lines added inside the dialog also get their
   *  "Campaña X%" badge + allowance skip. */
  campaignByArticulo: Map<string, number>;
  /** % factura y recargo MercadoPago de la venta: los cobros ya traen el
   *  recargo, así que el piso se mide contra total + recargo (como el server). */
  porcentajeFactura?: number;
  recargoMpPct?: number;
  /** El cliente de la venta es "Cliente del Exterior": exportación, líneas sin IVA (Factura E). */
  clienteExterior?: boolean;
}) {
  const qc = useQueryClient();

  // --- reference data (only fetched while the dialog is open) ---
  const articulosQ = useQuery({
    queryKey: ['editar-cotizacion', 'articulos'],
    queryFn: listActiveCotizableArticulos,
    staleTime: 60_000,
    enabled: open,
  });
  const inventarioQ = useQuery({
    queryKey: ['editar-cotizacion', 'inventario'],
    queryFn: listActiveInventario,
    staleTime: 60_000,
    enabled: open,
    // Refetch each time the dialog opens — stock is mutated in the
    // Inventario/Artículos module, which doesn't invalidate this key.
    refetchOnMount: 'always',
  });
  const fechasPorIngresarQ = useFechasPorIngresar(open);
  const descuentosMesQ = useQuery({
    queryKey: ['editar-cotizacion', 'descuentos-mes', vendedorApp, currentMesAnio()],
    queryFn: () =>
      vendedorApp
        ? listDescuentosByVendedorMes(vendedorApp, currentMesAnio())
        : Promise.resolve([]),
    enabled: open && Boolean(vendedorApp),
    staleTime: 60_000,
  });
  const parametrosQ = useQuery({
    queryKey: ['editar-cotizacion', 'parametros'],
    queryFn: listAllParametrosComDesc,
    staleTime: 5 * 60_000,
    enabled: open,
  });
  const regalosMesQ = useQuery({
    queryKey: ['editar-cotizacion', 'regalos-mes', vendedorApp, currentMesAnio()],
    queryFn: () =>
      vendedorApp ? getRegalosMes(vendedorApp, currentMesAnio()) : Promise.resolve(0),
    enabled: open && Boolean(vendedorApp),
    staleTime: 60_000,
  });
  // Saldo floor: any non-anulado comprobante (pagos + promesas) caps
  // how low the new total can go. Loaded only when the dialog is open
  // — pre-Aceptado ventas have no comprobantes so the sum is just 0.
  const comprobantesQ = useQuery({
    queryKey: ['editar-cotizacion', 'comprobantes', ventaId],
    queryFn: () => listActiveComprobantesByOperacion(ventaId),
    enabled: open && Boolean(ventaId),
    staleTime: 30_000,
  });
  // Pending discount approvals on this venta. Powers the "Pendiente
  // Aprobación" pill on lines whose discount already has an in-flight
  // request. The server-side createItem overwrites existing pending
  // rows on re-save, so the pill mainly serves to inform the seller
  // (the alternative was silently regenerating a fresh approval each
  // time they re-edit the line, which left admins with stacks of
  // duplicates in the queue).
  const pendingApprovalsQ = useQuery({
    queryKey: ['editar-cotizacion', 'pending-approvals', ventaId],
    queryFn: () => listPendingByOperacion(ventaId),
    enabled: open && Boolean(ventaId),
    staleTime: 30_000,
  });
  // Map of `IDDetalle_AD` (the sales_line SP id) → true. EditorLineSeed
  // carries the same id as `detalleId`, so a constant-time lookup
  // drives the per-row pill below.
  const pendingApprovalByDetalleId = useMemo(() => {
    const m = new Map<string, true>();
    for (const r of pendingApprovalsQ.data ?? []) {
      const id = r.fields.IDDetalle_AD?.trim();
      if (id) m.set(id, true);
    }
    return m;
  }, [pendingApprovalsQ.data]);
  // MX Nova "usado" context — fetched on dialog open so we can locally
  // project the totalFactura as the seller stages discounts and warn
  // BEFORE saving. Returns `applies=false` on AR / non-Nova MX ventas
  // so the warning never renders outside MX Nova flow.
  const usadoCtxQ = useQuery({
    queryKey: ['editar-cotizacion', 'usado-context', ventaId],
    queryFn: () => getUsadoContext(ventaId),
    enabled: open && Boolean(ventaId),
    staleTime: 30_000,
  });
  const usadoCtx = usadoCtxQ.data ?? null;
  // Keep the saldo pill live while the dialog is open: if admin
  // confirms a pago or another vendedor registers a new comprobante in
  // parallel, the floor moves immediately so the user can react before
  // hitting the server's `below_paid` 409.
  useRealtimeInvalidate(
    ['sales_payment_receipts', 'sales_headers'],
    [['editar-cotizacion', 'comprobantes', ventaId]],
  );
  const saldoCobrado = useMemo(
    () => sumComprobantesEnVentaCurrency(comprobantesQ.data ?? [], moneda),
    [comprobantesQ.data, moneda],
  );

  const inventarioByArticulo = useMemo(() => {
    const m = new Map<string, InventarioItem>();
    for (const inv of inventarioQ.data ?? []) {
      const aid = inv.fields.IDArticulo_I;
      if (aid) m.set(aid, inv);
    }
    return m;
  }, [inventarioQ.data]);

  const limits = useMemo(() => readAllowanceLimits(parametrosQ.data ?? []), [parametrosQ.data]);
  const bracketPercents = useMemo(
    () => readBracketPercents(parametrosQ.data ?? []),
    [parametrosQ.data],
  );
  const regaloLimit = useMemo(
    () => readRegaloMensualLimit(parametrosQ.data ?? []),
    [parametrosQ.data],
  );
  const monthlyCounts = useMemo(
    () => countMonthlyDescuentos(descuentosMesQ.data ?? []),
    [descuentosMesQ.data],
  );

  const { config: activeCountry } = useActiveCountry();

  // --- editor state ---
  const [draftLines, setLines] = useState<EditorLine[]>([]);
  // Cliente del exterior (Factura E): todas las líneas a 0% de IVA. `iva` del
  // borrador conserva la alícuota que traía la línea / el artículo; acá sólo se
  // aplica según el cliente de la venta.
  const lines = useMemo(
    () => conIvaSegunCliente(draftLines, clienteExterior),
    [draftLines, clienteExterior],
  );
  const [articuloDraft, setArticuloDraft] = useState<ArticuloItem | null>(null);
  const [cantidadDraft, setCantidadDraft] = useState<number>(1);
  const [giftArmed, setGiftArmed] = useState(false);
  const [garantiaArmed, setGarantiaArmed] = useState(false);
  /** Artículo sin duración de garantía esperando la fecha manual. */
  const [garantiaPendiente, setGarantiaPendiente] = useState<string | null>(null);
  const [authDialog, setAuthDialog] = useState<AuthDialogState | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Seed/reset state when the dialog opens (or a different venta loads).
  // We intentionally do NOT depend on `initialLines` reference changes —
  // the parent rebuilds that array whenever any of its sibling queries
  // refetch, and re-seeding wipes the user's in-progress edits. Tracking
  // `ventaId` is enough: it identifies the venta unambiguously.
  //
  // `stockDisponible` is hydrated from the cached inventario map at seed
  // time so the badge isn't briefly wrong on open; live refreshes after
  // open are handled by the second effect.
  useEffect(() => {
    if (!open) return;
    setLines(
      initialLines.map((l, idx) => ({
        key: `${l.detalleId}-${idx}`,
        detalleId: l.detalleId,
        articuloId: l.articuloId,
        articulo: l.articulo,
        monto: l.monto,
        cantidad: l.cantidad,
        iva: l.iva,
        descuento: l.descuento,
        esRegalo: l.esRegalo,
        llevaGarantia: l.llevaGarantia,
        garantiaMeses: l.garantiaMeses,
        garantiaVence: l.garantiaVence,
        campaignPct: l.campaignPct,
        motivo: l.motivo ?? '',
        approvedDescuento: l.approvedDescuento,
        fechaEstimadaIngreso: l.fechaEstimadaIngreso ?? null,
        stockDisponible: calcStockCotizable(inventarioByArticulo.get(l.articuloId)),
      })),
    );
    setArticuloDraft(null);
    setCantidadDraft(1);
    setGiftArmed(false);
    setGarantiaArmed(false);
    setGarantiaPendiente(null);
    setAuthDialog(null);
    setErrorMsg(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ventaId]);

  // Refresh `stockDisponible` on each line as inventario refetches mid-edit
  // — without wiping the user's other in-progress fields.
  useEffect(() => {
    if (inventarioByArticulo.size === 0) return;
    setLines((prev) =>
      prev.map((l) => ({
        ...l,
        stockDisponible: calcStockCotizable(inventarioByArticulo.get(l.articuloId)),
      })),
    );
  }, [inventarioByArticulo]);

  // --- add / update / remove ---
  /** `venceManual`: vencimiento puesto a mano cuando el producto no tiene duración de garantía. */
  const addLine = (venceManual?: string) => {
    if (!articuloDraft || cantidadDraft <= 0) return;
    const mesesGarantia = mesesGarantiaDe(articuloDraft.fields.GarantiaMeses_ART);
    if (garantiaArmed && mesesGarantia == null && !venceManual) {
      setGarantiaPendiente(articuloDraft.fields.Articulo_ART?.trim() || 'Artículo');
      return;
    }
    const garantia: GarantiaLinea = garantiaArmed
      ? { llevaGarantia: true, garantiaMeses: mesesGarantia, garantiaVence: mesesGarantia == null ? venceManual! : null }
      : SIN_GARANTIA;
    const inv = inventarioByArticulo.get(articuloDraft.id);
    const total = calcStockCotizable(inv);
    const used = usedByArticulo.get(articuloDraft.id) ?? 0;
    const remaining = Math.max(0, total - used);
    if (cantidadDraft > remaining) {
      toast.error('Stock insuficiente', {
        description: `${articuloDraft.fields.Articulo_ART?.trim() || 'Artículo'} — disponible: ${remaining} Un.`,
      });
      return;
    }
    const campaignPct = campaignByArticulo.get(articuloDraft.id) ?? null;
    // Only accessories (Cuenta Contable = Accesorios) may be gifted; guard
    // here too so a lingering `giftArmed` can't gift a non-accesorio.
    const applyGift =
      giftArmed && isArticuloRegalable(articuloDraft) && monthlyGiftOk;
    const initialDescuento = applyGift ? 100 : campaignPct ?? 0;
    // Serialized articles are one unit per line (own NroSerie at despacho) —
    // split a cantidad>1 add into N qty-1 lines.
    const llevaNroSerie = articuloLlevaNroSerie(articuloDraft);
    const lineCount = llevaNroSerie ? cantidadDraft : 1;
    const perLineCantidad = llevaNroSerie ? 1 : cantidadDraft;
    const newLines: EditorLine[] = Array.from({ length: lineCount }, (_, i) => ({
      key: `new-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 7)}`,
      detalleId: undefined,
      articuloId: articuloDraft.id,
      articulo: articuloDraft.fields.Articulo_ART?.trim() || '—',
      monto: articuloPrice(articuloDraft, moneda),
      cantidad: perLineCantidad,
      iva: parseIvaFromArticulo(articuloDraft.fields.IVA_ART),
      descuento: initialDescuento,
      esRegalo: applyGift,
      campaignPct,
      motivo: '',
      stockDisponible: total,
      fechaEstimadaIngreso: null,
      ...garantia,
    }));
    setLines((prev) => [...prev, ...newLines]);
    setArticuloDraft(null);
    setCantidadDraft(1);
    setGiftArmed(false);
    setGarantiaArmed(false);
  };

  const updateLine = (key: string, patch: Partial<EditorLine>) => {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  };
  const removeLine = (key: string) => setLines((prev) => prev.filter((l) => l.key !== key));

  const handleDescuentoCommit = (key: string, previousDescuento: number) => {
    const line = lines.find((l) => l.key === key);
    if (!line) return;
    // Lines whose current value matches the descuento already approved
    // on the live row don't trigger a fresh approval prompt — the
    // approval is still valid until the user picks a different number.
    if (line.approvedDescuento != null && line.descuento === line.approvedDescuento) {
      if (line.motivo) updateLine(key, { motivo: '' });
      return;
    }
    const check = evaluateLineDiscount({
      descuento: line.descuento,
      esRegalo: line.esRegalo,
      campaignPct: line.campaignPct,
      bracketPercents,
      monthlyCounts,
      limits,
    });
    if (!check.needsAuthorization) {
      if (line.motivo) updateLine(key, { motivo: '' });
      return;
    }
    if (line.motivo && previousDescuento === line.descuento) return;
    setAuthDialog({
      lineKey: key,
      articulo: line.articulo,
      descuento: line.descuento,
      motivo: line.motivo,
      previousDescuento,
      previousMotivo: line.motivo,
      suggested: check.motivo,
    });
  };

  const confirmAuthDialog = () => {
    if (!authDialog) return;
    updateLine(authDialog.lineKey, { motivo: authDialog.motivo.trim() });
    setAuthDialog(null);
  };
  const cancelAuthDialog = () => {
    if (!authDialog) return;
    updateLine(authDialog.lineKey, {
      descuento: authDialog.previousDescuento,
      motivo: authDialog.previousMotivo,
    });
    setAuthDialog(null);
  };

  // --- derived ---
  const linesWithComputed = useMemo(() => {
    const extraUsed = { A: 0, B: 0, C: 0 };
    return lines.map((l) => {
      const totals = calcLineTotals(l);
      const baseCheck = evaluateLineDiscount({
        descuento: l.descuento,
        esRegalo: l.esRegalo,
        campaignPct: l.campaignPct,
        bracketPercents,
        monthlyCounts,
        limits,
        extraUsed,
      });
      // Already-approved value on a live row → exempt from re-auth.
      // Skip the bracket counter increment too: the existing Descuentos
      // row is already counted in `monthlyCounts`, so adding it again
      // here would double-count the same approval.
      const alreadyApproved =
        l.approvedDescuento != null && l.descuento === l.approvedDescuento;
      const check = alreadyApproved
        ? { ...baseCheck, needsAuthorization: false, motivo: '' }
        : baseCheck;
      if (!alreadyApproved) {
        if (check.category === 'A') extraUsed.A += 1;
        else if (check.category === 'B') extraUsed.B += 1;
        else if (check.category === 'C') extraUsed.C += 1;
      }
      return { line: l, totals, check };
    });
  }, [lines, monthlyCounts, limits, bracketPercents]);

  const invoice = useMemo(() => calcInvoiceTotals(lines), [lines]);

  // Gift gating: a line can only be marked Regalo when (a) the cotización
  // Total (con IVA) reaches the configured minimum for this currency, AND
  // (b) the selected article's Cuenta Contable is Accesorios.
  const regaloMinimo = useRegaloMinimoThreshold(moneda);
  // Gift threshold compares against the SUBTOTAL (sin IVA), not the total —
  // consistent across IVA rates. Mirrors NuevaCotizacionPage.
  const montoOk = regaloMinimo <= 0 || invoice.subtotalDescontado >= regaloMinimo;
  const draftIsAccesorio = isArticuloRegalable(articuloDraft);
  // Monthly gift quota for this venta's vendor: gifts already persisted this
  // month (which include this venta's existing regalo lines) plus the NEW
  // regalo lines added in this editor (those without a detalleId — existing
  // ones are already in the server count). `regaloLimit <= 0` = sin límite.
  const giftsUsedThisMonth =
    (regalosMesQ.data ?? 0) +
    lines.filter((l) => l.esRegalo && !l.detalleId).reduce((s, l) => s + l.cantidad, 0);
  // Block BEFORE adding: gifting the toolbar article adds `cantidadDraft`
  // units, so check they still fit under the cap (used + about-to-add ≤
  // limit), not merely that we're under it now. Mirrors NuevaCotizacionPage.
  const monthlyGiftOk =
    regaloLimit <= 0 || giftsUsedThisMonth + Math.max(1, cantidadDraft) <= regaloLimit;
  const giftEnabled = montoOk && draftIsAccesorio && monthlyGiftOk;
  // Reason-specific hint shown on hover over the Regalo button (Radix
  // Tooltip) and used as its aria-label. Mirrors NuevaCotizacionPage.
  const giftsRemaining = Math.max(0, regaloLimit - giftsUsedThisMonth);
  const giftHint = !montoOk
    ? `Para regalar, el subtotal (sin IVA) debe ser ≥ ${formatMoney(regaloMinimo, moneda)} ${moneda}`
    : !draftIsAccesorio
      ? 'Solo se pueden regalar accesorios'
      : !monthlyGiftOk
        ? giftsRemaining <= 0
          ? `Alcanzaste el límite de regalos del mes (${regaloLimit})`
          : `Te ${giftsRemaining === 1 ? 'queda 1 regalo' : `quedan ${giftsRemaining} regalos`} este mes — reducí la cantidad`
        : giftArmed
          ? 'Próximo artículo será regalo'
          : 'Marcar próximo artículo como regalo';
  useEffect(() => {
    if (!giftEnabled && giftArmed) setGiftArmed(false);
  }, [giftEnabled, giftArmed]);

  // Sum of cantidades already committed to the draft, by article. Lets
  // the toolbar's stock check subtract them from the global Disp +
  // PorIngresar pool so two consecutive lines on the same article
  // can't collectively oversell the stock.
  const usedByArticulo = useMemo(() => {
    const m = new Map<string, number>();
    for (const l of lines) {
      m.set(l.articuloId, (m.get(l.articuloId) ?? 0) + l.cantidad);
    }
    return m;
  }, [lines]);

  const draftStock = useMemo(() => {
    if (!articuloDraft) return null;
    const total = calcStockCotizable(inventarioByArticulo.get(articuloDraft.id));
    const used = usedByArticulo.get(articuloDraft.id) ?? 0;
    return Math.max(0, total - used);
  }, [articuloDraft, inventarioByArticulo, usedByArticulo]);

  // Mirror of NuevaCotizacionPage's article picker: show in-stock items
  // first (A→Z), push out-of-stock to the bottom (rendered in red via
  // `getItemClassName`). Available = global cotizable pool minus quantities
  // already drafted on this edit.
  const articleStockById = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of articulosQ.data ?? []) {
      const total = calcStockCotizable(inventarioByArticulo.get(a.id));
      const used = usedByArticulo.get(a.id) ?? 0;
      m.set(a.id, Math.max(0, total - used));
    }
    return m;
  }, [articulosQ.data, inventarioByArticulo, usedByArticulo]);

  // Disponible vs. Por Ingresar de un artículo (el cotizable es la suma).
  const stockDe = (articuloId: string) => {
    const inv = inventarioByArticulo.get(articuloId);
    const disponible = Math.max(0, calcStockDisponible(inv));
    const porIngresar = Math.max(0, calcStockCotizable(inv) - disponible);
    return { disponible, porIngresar };
  };
  // Celeste = la línea depende de stock por ingresar (el over-stock en rojo manda).
  const estadoPorLinea = useMemo(
    () => estadosPorLinea(lines, stockDe),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lines, inventarioByArticulo],
  );

  // Serialized-article lookup (`LlevaNroSerie_ART='SI'`). Drives the
  // one-unit-per-line rule: `addLine` auto-splits and the in-table cantidad
  // is locked for these lines. Resolved reactively so legacy seeded lines
  // (loaded before the articulos query settles) lock correctly too.
  const serializedById = useMemo(() => {
    const m = new Map<string, boolean>();
    for (const a of articulosQ.data ?? []) {
      if (articuloLlevaNroSerie(a)) m.set(a.id, true);
    }
    return m;
  }, [articulosQ.data]);

  // Per-article costoContable lookup — used to evaluate the MX Nova
  // "usado" predicate live as the seller stages discounts. The article
  // payload already carries `CostoContable_ART` (comma-decimal text);
  // we parse once into a map. AR articles return 0 — the predicate
  // short-circuits later via `usadoCtx.applies`.
  const costoByArticleId = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of articulosQ.data ?? []) {
      const raw = (a.fields.CostoContable_ART ?? '').toString().trim();
      if (!raw) continue;
      const n = Number(raw.replace(',', '.'));
      if (Number.isFinite(n) && n >= 0) m.set(a.id, n);
    }
    return m;
  }, [articulosQ.data]);

  // Live "would-be usado" predicate at the venta level. Projected from
  // the in-progress EditorLine state + the persisted porcentaje_factura
  // on the venta. Re-runs on every line edit so the banner tracks
  // keystrokes. Per-line pill was removed (2026-05-19) — usado is a
  // venta-wide state, and stacking a per-line "Usado" next to
  // "Aprobación Requerida" overflowed the cell layout.
  const usadoEval = useMemo(() => {
    if (!usadoCtx?.applies) {
      return { ventaUsado: false, facturaMinimo: 0 };
    }
    const porcentaje = Math.max(0, Math.min(100, usadoCtx.porcentajeFactura));
    const fraction = porcentaje / 100;
    const rateMult = (1 + usadoCtx.comisionNova) * (1 + usadoCtx.ivaMx);
    let ventaCosto = 0;
    let ventaFactura = 0;
    for (const l of lines) {
      const ivaRate = Number(l.iva) || 0;
      const cantidad = Number(l.cantidad) || 0;
      const base = cantidad * (l.monto ?? 0) * (1 - (Number(l.descuento) || 0) / 100);
      const totalCotLine = base * (1 + ivaRate / 100);
      ventaFactura += totalCotLine * fraction;
      ventaCosto += cantidad * (costoByArticleId.get(l.articuloId) ?? 0);
    }
    const facturaMinimo = ventaCosto * rateMult;
    // Gate on `porcentaje > 0` (not `ventaFactura > 0`): a 100%
    // discount on every line zeros out `ventaFactura` but the factura
    // side still exists — that IS usado territory (an extreme one).
    // The original E1 guard against all-cash ventas (porc=0) is
    // preserved because porc=0 fails the same condition. Fixed
    // 2026-05-19 after QA noticed the banner disappeared at 100% off.
    const ventaUsado =
      facturaMinimo > 0 && porcentaje > 0 && ventaFactura < facturaMinimo;
    return { ventaUsado, facturaMinimo };
  }, [usadoCtx, lines, costoByArticleId]);

  const sortedArticulos = useMemo(() => {
    const items = [...(articulosQ.data ?? [])];
    return items.sort((a, b) => {
      const sa = (articleStockById.get(a.id) ?? 0) > 0 ? 0 : 1;
      const sb = (articleStockById.get(b.id) ?? 0) > 0 ? 0 : 1;
      if (sa !== sb) return sa - sb;
      const la = (a.fields.Articulo_ART ?? a.fields.Concat_ART ?? '').trim();
      const lb = (b.fields.Articulo_ART ?? b.fields.Concat_ART ?? '').trim();
      return la.localeCompare(lb, 'es', { sensitivity: 'base' });
    });
  }, [articulosQ.data, articleStockById]);
  const draftOverStock = draftStock !== null && cantidadDraft > draftStock;

  const anyAuthMissingMotivo = linesWithComputed.some(
    ({ line, check }) => check.needsAuthorization && !line.motivo.trim(),
  );
  // Floor enforcement (frontend mirror of the backend `below_paid`
  // check). Active only when there's actually been a pago/promesa —
  // otherwise saldoCobrado is 0 and the comparison is a no-op.
  const totalConRecargo =
    invoice.total +
    (recargoMpPct > 0 ? round2((invoice.total * porcentajeFactura * recargoMpPct) / 10_000) : 0);
  const totalBelowPaid = saldoCobrado > 0 && totalConRecargo + 0.01 < saldoCobrado;
  const canSave =
    lines.length > 0 &&
    !anyAuthMissingMotivo &&
    !totalBelowPaid &&
    (montoOk || !lines.some((l) => l.esRegalo));

  // --- mutation ---
  const mutation = useMutation({
    mutationFn: async () => {
      const payloadLines: EditVentaLinePayload[] = linesWithComputed.map(
        ({ line, totals, check }) => ({
          detalleId: line.detalleId,
          articuloId: line.articuloId,
          articulo: line.articulo,
          monto: line.monto,
          cantidad: line.cantidad,
          iva: ivaAEnviar(line.iva, clienteExterior, activeCountry.ivaRateDefault),
          descuento: line.descuento,
          esRegalo: line.esRegalo,
          llevaGarantia: line.llevaGarantia,
          garantiaVence: line.garantiaVence,
          // Sólo si la línea sigue dependiendo de stock por ingresar. Sin
          // cambios respecto de lo guardado = se omite (no toca la columna).
          fechaEstimadaIngreso: (() => {
            const nueva =
              estadoPorLinea.get(line.key) === 'por-ingresar' ? line.fechaEstimadaIngreso ?? null : null;
            const guardada = initialLines.find((i) => i.detalleId === line.detalleId)?.fechaEstimadaIngreso ?? null;
            return nueva === guardada ? undefined : nueva;
          })(),
          subtotal: totals.subtotal,
          subtotalDescontado: totals.subtotalDescontado,
          total: totals.total,
          needsAuthorization: check.needsAuthorization,
          tipoDescuentoLabel: check.tipoLabel,
          motivoAutorizacion: line.motivo.trim() || check.motivo,
          campaignPct: line.campaignPct,
        }),
      );
      return editarVenta(ventaId, {
        header: {
          moneda,
          subtotal: invoice.subtotal,
          iva21: invoice.iva21,
          iva105: invoice.iva105,
          total: invoice.total,
        },
        lines: payloadLines,
      });
    },
    onSuccess: () => {
      toast.success('Cotización actualizada');
      qc.invalidateQueries({ queryKey: ['detalle-venta'] });
      qc.invalidateQueries({ queryKey: ['ventas'] });
      // Edits can add or remove AutorizacionDescuentos rows as lines change.
      qc.invalidateQueries({ queryKey: ['aprobaciones'] });
      // Refresh the vendor's monthly gift count if regalo lines changed.
      qc.invalidateQueries({ queryKey: ['editar-cotizacion', 'regalos-mes'] });
      onClose();
    },
    onError: (err) => {
      // eslint-disable-next-line no-console
      console.error('[ventas] editarVenta failed', err);
      // Server-side floor / status race conditions carry actionable
      // messages — surface them verbatim instead of the generic copy.
      if (err instanceof ApiError && (err.code === 'below_paid' || err.code === 'not_editable')) {
        setErrorMsg(err.message);
        return;
      }
      // Discount/regalo/campaña rule changed under the seller → refetch the
      // reference data so the form re-evaluates, then ask them to review.
      const stale = staleConfigError(err);
      if (stale) {
        qc.invalidateQueries({ queryKey: ['editar-cotizacion', 'parametros'] });
        qc.invalidateQueries({ queryKey: ['editar-cotizacion', 'descuentos-mes'] });
        qc.invalidateQueries({ queryKey: ['editar-cotizacion', 'regalos-mes'] });
        // campaignByArticulo is sourced from the parent DetalleVentaPage.
        qc.invalidateQueries({ queryKey: ['detalle-venta'] });
        setErrorMsg(staleConfigMessage(stale));
        return;
      }
      setErrorMsg(
        'No se pudieron guardar los cambios. Intentá nuevamente en unos minutos.',
      );
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !mutation.isPending) onClose();
      }}
    >
      <DialogContent hideClose={mutation.isPending} className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>Editar cotización</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          {usadoEval.ventaUsado && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs leading-relaxed text-rose-900">
              <span className="font-semibold uppercase tracking-wide text-rose-700">
                ⚠ Advertencia ·
              </span>{' '}
              Con estos descuentos la venta queda por debajo del costo
              contable + comisión + IVA (mínimo{' '}
              {formatMoney(usadoEval.facturaMinimo, moneda)}) y se
              registrará como <strong>usado</strong>.
            </div>
          )}
          {/* ADD-ARTICLE TOOLBAR */}
          <div className="flex flex-wrap items-end gap-2 rounded-xl border bg-card p-3">
            <div className="w-72">
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Artículo
              </label>
              <Combobox
                value={articuloDraft}
                onChange={setArticuloDraft}
                items={sortedArticulos}
                getKey={(a) => a.id}
                getLabel={(a) => a.fields.Articulo_ART?.trim() || a.fields.Concat_ART || '—'}
                getSublabel={(a) => {
                  // Campaign info lives in the right-aligned badge
                  // (renderItemRight) so it never gets truncated here.
                  const price = `${formatMoney(articuloPrice(a, moneda), moneda)} ${moneda}`;
                  const stock = articleStockById.get(a.id) ?? 0;
                  const { disponible, porIngresar } = stockDe(a.id);
                  const used = usedByArticulo.get(a.id) ?? 0;
                  if (estadoSiguienteUnidad(disponible, porIngresar, used) === 'por-ingresar') {
                    const dispRest = Math.max(0, disponible - used);
                    return `${price} · Disp: ${dispRest} · Por ingresar: ${Math.max(0, stock - dispRest)}`;
                  }
                  const stockLabel = stock > 0 ? `Stock: ${stock}` : 'Sin stock';
                  return `${price} · ${stockLabel}`;
                }}
                renderItemRight={(a) => {
                  const campaignPct = campaignByArticulo.get(a.id);
                  if (campaignPct == null) return null;
                  return (
                    <span
                      className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700"
                      title={`Campaña activa: ${campaignPct}% off`}
                    >
                      <BadgePercent className="h-3 w-3" aria-hidden />
                      {campaignPct}%
                    </span>
                  );
                }}
                getItemClassName={(a) => {
                  if ((articleStockById.get(a.id) ?? 0) <= 0) return CLASE_ITEM_SIN_STOCK;
                  const { disponible, porIngresar } = stockDe(a.id);
                  const used = usedByArticulo.get(a.id) ?? 0;
                  return estadoSiguienteUnidad(disponible, porIngresar, used) === 'por-ingresar'
                    ? CLASE_ITEM_POR_INGRESAR
                    : undefined;
                }}
                matcher={(a, q) =>
                  (a.fields.Articulo_ART ?? '').toLowerCase().includes(q) ||
                  (a.fields.Codigo_ART ?? '').toLowerCase().includes(q) ||
                  (a.fields.Concat_ART ?? '').toLowerCase().includes(q)
                }
                contentClassName={ANCHO_LISTA_ARTICULOS}
                placeholder="Seleccionar artículo"
              />
            </div>
            <div className="w-24">
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Cantidad
              </label>
              <Input
                type="number"
                min={1}
                step={1}
                variant="filled"
                aria-invalid={draftOverStock}
                className={cn(
                  'h-10',
                  draftOverStock &&
                    'border border-destructive bg-destructive/10 text-destructive focus-visible:border-destructive',
                )}
                value={cantidadDraft}
                onChange={(e) =>
                  setCantidadDraft(Math.max(1, Number(e.target.value) || 1))
                }
              />
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                {/* span wrapper: a disabled button has pointer-events-none,
                    so hover passes through to this span and the tooltip
                    still fires when the gift is gated. */}
                <span className="inline-flex">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    disabled={!giftEnabled}
                    onClick={() => setGiftArmed((v) => !v)}
                    aria-label={giftHint}
                    className={cn(
                      'h-10 w-10 disabled:cursor-not-allowed disabled:opacity-50',
                      giftArmed &&
                        'border-foreground bg-foreground text-background hover:bg-foreground/90 hover:text-background',
                    )}
                  >
                    <Gift className="h-4 w-4" aria-hidden />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>{giftHint}</TooltipContent>
            </Tooltip>
            <GarantiaToggle armed={garantiaArmed} onToggle={() => setGarantiaArmed((v) => !v)} />
            <Button
              type="button"
              size="icon"
              className="h-10 w-10 bg-foreground text-background hover:bg-foreground/90"
              onClick={() => addLine()}
              disabled={!articuloDraft || cantidadDraft < 1 || draftOverStock}
              title={
                draftOverStock ? 'Cantidad supera el stock disponible' : 'Agregar artículo'
              }
            >
              <Plus className="h-4 w-4" aria-hidden />
            </Button>
          </div>

          {/* Gift-gating reasons surface on hover over the Regalo button
              (its `title`), so no inline note here. */}

          {/* LINES TABLE */}
          {lines.length === 0 ? (
            <div className="rounded-xl border border-dashed bg-muted/30 p-6 text-center text-sm text-muted-foreground">
              No hay artículos en la cotización.
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border">
              <table className="w-full text-sm">
                <thead className="bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-semibold">Artículo</th>
                    <th className="w-28 px-3 py-2 text-right font-semibold">Monto</th>
                    <th className="w-24 px-3 py-2 font-semibold">Cantidad</th>
                    <th className="w-28 px-3 py-2 text-right font-semibold">Subtotal</th>
                    <th className="w-14 px-3 py-2 font-semibold">IVA</th>
                    <th className="w-24 px-3 py-2 font-semibold">Descuento</th>
                    <th className="w-28 px-3 py-2 text-right font-semibold">Total</th>
                    <th className="w-14 px-3 py-2 text-right font-semibold">Acc.</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {linesWithComputed.map(({ line, totals, check }) => {
                    // Aggregate per-article: a single article spread
                    // across multiple draft lines must not exceed its
                    // cotizable total (Disponible + Por Ingresar).
                    const articleSum = usedByArticulo.get(line.articuloId) ?? 0;
                    const overStock = articleSum > line.stockDisponible;
                    const porIngresar = !overStock && estadoPorLinea.get(line.key) === 'por-ingresar';
                    return (
                      <tr
                        key={line.key}
                        className={overStock ? 'bg-rose-50/60' : porIngresar ? 'bg-sky-50/60' : undefined}
                      >
                        <td className="px-3 py-2 align-middle">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium text-foreground">
                              {line.articulo}
                            </span>
                            {line.esRegalo && (
                              <span className="inline-flex items-center gap-1 rounded-md bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-violet-700">
                                <Gift className="h-3 w-3" aria-hidden /> Regalo
                              </span>
                            )}
                            <GarantiaBadge
                              linea={line}
                              onQuitar={() => updateLine(line.key, SIN_GARANTIA)}
                            />
                            {line.campaignPct != null && !line.esRegalo && (
                              <span className="inline-flex items-center gap-1 rounded-md bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-emerald-700">
                                <BadgePercent className="h-3 w-3" aria-hidden /> Campaña{' '}
                                {line.campaignPct}%
                              </span>
                            )}
                            {check.needsAuthorization && (
                              <span className="inline-flex items-center gap-1 rounded-md bg-fuchsia-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-fuchsia-700">
                                Aprobación Requerida
                              </span>
                            )}
                            {line.detalleId &&
                              pendingApprovalByDetalleId.has(line.detalleId) && (
                                <span
                                  className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-800"
                                  title="Esta línea tiene una solicitud de aprobación pendiente. Si volvés a editar el descuento, se sobrescribe la solicitud existente."
                                >
                                  Aprobación Pendiente
                                </span>
                              )}
                            {porIngresar && (
                              <>
                                <PorIngresarBadge />
                                <FechaIngresoPicker
                                  opciones={fechasPorIngresarQ.data?.[line.articuloId]}
                                  value={line.fechaEstimadaIngreso ?? null}
                                  onChange={(f) => updateLine(line.key, { fechaEstimadaIngreso: f })}
                                />
                              </>
                            )}
                            {overStock && (
                              <span className="inline-flex items-center gap-1 rounded-md bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-rose-700">
                                <AlertTriangle className="h-3 w-3" aria-hidden /> Stock:{' '}
                                {line.stockDisponible} Un.
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right align-middle">
                          <MoneyCell amount={line.monto} moneda={moneda} />
                        </td>
                        <td className="px-3 py-2 align-middle">
                          {serializedById.get(line.articuloId) ? (
                            // Serialized article: 1 unit per line (own
                            // NroSerie). Add the article again to sum units.
                            <Input
                              type="text"
                              variant="filled"
                              disabled
                              readOnly
                              className="h-9 w-20 cursor-not-allowed opacity-70"
                              title="Artículo serializado: 1 unidad por línea. Agregá el artículo de nuevo para sumar unidades."
                              value={String(line.cantidad)}
                            />
                          ) : (
                            <Input
                              type="number"
                              min={1}
                              step={1}
                              variant="filled"
                              className="h-9 w-20"
                              value={line.cantidad}
                              onChange={(e) =>
                                updateLine(line.key, {
                                  cantidad: Math.max(1, Number(e.target.value) || 1),
                                })
                              }
                            />
                          )}
                        </td>
                        <td className="px-3 py-2 text-right align-middle">
                          <MoneyCell amount={totals.subtotal} moneda={moneda} />
                        </td>
                        <td className="px-3 py-2 align-middle text-muted-foreground">
                          {line.iva === 0 ? '—' : `${String(line.iva).replace('.', ',')}%`}
                        </td>
                        <td className="px-3 py-2 align-middle">
                          <DescuentoCell
                            descuento={line.descuento}
                            esRegalo={line.esRegalo}
                            onChange={(next) => updateLine(line.key, { descuento: next })}
                            onCommit={(prev) => handleDescuentoCommit(line.key, prev)}
                          />
                        </td>
                        <td className="px-3 py-2 text-right align-middle">
                          <MoneyCell amount={totals.total} moneda={moneda} emphasise />
                        </td>
                        <td className="px-3 py-2 text-right align-middle">
                          <button
                            type="button"
                            onClick={() => removeLine(line.key)}
                            title="Eliminar"
                            aria-label="Eliminar"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-destructive/30 bg-destructive/10 text-destructive hover:bg-destructive/20"
                          >
                            <Trash2 className="h-4 w-4" aria-hidden />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* TOTALS STRIP */}
          <div className="flex flex-wrap items-center gap-3 rounded-xl border bg-card p-3">
            {clienteExterior && (
              <p className="w-full text-xs font-medium text-muted-foreground">
                {NOTA_EXPORTACION_SIN_IVA}
              </p>
            )}
            <TotalPill label="Subtotal" value={invoice.subtotalDescontado} moneda={moneda} />
            {Object.entries(invoice.ivaByRate)
              .map(([k, v]) => [Number(k), v] as const)
              .sort((a, b) => b[0] - a[0])
              .map(([rate, amount]) => (
                <TotalPill
                  key={rate}
                  label={`IVA ${formatIvaRate(rate)}%`}
                  value={amount}
                  moneda={moneda}
                />
              ))}
            <TotalPill label="Total" value={invoice.total} moneda={moneda} emphasise />
            {saldoCobrado > 0 && (
              <TotalPill
                label="Cobrado / Comprometido"
                value={saldoCobrado}
                moneda={moneda}
              />
            )}
          </div>

          {totalBelowPaid && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-xl border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <div className="leading-snug">
                El total editado ({formatMoney(totalConRecargo, moneda)}) es menor al monto ya
                registrado en pagos y promesas ({formatMoney(saldoCobrado, moneda)}). Anulá un
                comprobante en la pestaña Pagos o aumentá el total para poder guardar.
              </div>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            disabled={!canSave || mutation.isPending}
            onClick={() => mutation.mutate()}
            className="gap-2 bg-foreground text-background hover:bg-foreground/90"
          >
            <Check className="h-4 w-4" aria-hidden /> Guardar cambios
          </Button>
        </DialogFooter>
      </DialogContent>

      <DescuentoAprobacionDialog
        state={authDialog}
        onMotivoChange={(next) =>
          setAuthDialog((prev) => (prev ? { ...prev, motivo: next } : prev))
        }
        onConfirm={confirmAuthDialog}
        onCancel={cancelAuthDialog}
        usadoWarning={usadoEval.ventaUsado}
      />
      <GarantiaFechaDialog
        articulo={garantiaPendiente}
        onCancel={() => setGarantiaPendiente(null)}
        onConfirm={(vence) => {
          setGarantiaPendiente(null);
          addLine(vence);
        }}
      />

      <ErrorDialog
        open={errorMsg !== null}
        message={errorMsg ?? ''}
        onClose={() => setErrorMsg(null)}
      />
      {mutation.isPending && <ProcesandoOverlay message="Guardando cambios…" />}
    </Dialog>
  );
}

type EditorLine = Omit<EditorLineSeed, 'detalleId' | 'motivo'> & {
  key: string;
  /** Present for existing detalle rows being edited; absent for lines
   *  added within this dialog session. */
  detalleId?: string;
  motivo: string;
  stockDisponible: number;
};

function TotalPill({
  label,
  value,
  moneda,
  emphasise,
}: {
  label: string;
  value: number;
  moneda: Moneda;
  emphasise?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex h-11 flex-col justify-center rounded-xl border bg-secondary px-4 leading-tight',
        emphasise && 'border-primary/30 bg-primary/5',
      )}
    >
      <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <MoneyCell amount={value} moneda={moneda} emphasise={emphasise} />
    </div>
  );
}

function pad(n: number) {
  return String(n).padStart(2, '0');
}
function currentMesAnio(): string {
  const d = new Date();
  return `${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

function parseIvaFromArticulo(iva: string | null | undefined): IvaRate {
  // Country-agnostic — accepts whatever rate the article carries.
  // 0 / unparseable → IVA Exento.
  const v = parseMoney(iva);
  return v != null && v > 0 ? v : 0;
}

function articuloPrice(a: ArticuloItem, moneda: Moneda): number {
  const raw = moneda === 'USD' ? a.fields.ValorUnDolar_ART : a.fields.ValorUn_ART;
  return parseMoney(raw) ?? 0;
}

/**
 * Sum every non-anulado comprobante (pago + promesa) expressed in the
 * venta's currency. Mirrors the backend `contribution()` rule: when the
 * medio's currency differs from the venta's, `Pesos_CP` carries the
 * already-converted amount; otherwise `Monto_CP` is already in the
 * venta currency.
 */
function sumComprobantesEnVentaCurrency(
  rows: ComprobantePagoItem[],
  moneda: Moneda,
): number {
  let total = 0;
  for (const c of rows) {
    if ((c.fields.Status_P ?? '') === 'Anulado') continue;
    const medio = c.fields.MedioPago_CP ?? '';
    const isUsdMedio =
      cajaBase(medio) === 'Efectivo Dolar' ||
      medio === 'Transferencia Galicia Dolar' ||
      medio === 'Transferencia Dolar';
    const raw =
      (moneda === 'ARS' && isUsdMedio) || (moneda === 'USD' && !isUsdMedio)
        ? parseMoney(c.fields.Pesos_CP) ?? parseMoney(c.fields.Monto_CP) ?? 0
        : parseMoney(c.fields.Monto_CP) ?? 0;
    total += raw;
  }
  return Math.round(total * 100) / 100;
}

